import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  CalendarDays,
  ChevronRight,
  Loader2,
  TrendingUp,
} from 'lucide-react';
import { useLocation } from 'wouter';
import { getPublicStudentScoreSample } from '../../lib/admin-api';
import AdminLayout from '../../components/admin/AdminLayout';
import { DEMO_TEACHER_BASE, demoEvaluationHref } from '../../lib/demo-teacher';
import { cn } from '../../lib/utils';

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function scoreTone(score: number | null): string {
  if (score == null) return 'bg-gray-100 text-gray-500';
  if (score >= 80) return 'bg-emerald-100 text-emerald-900';
  if (score >= 60) return 'bg-cyan-100 text-cyan-900';
  if (score >= 40) return 'bg-amber-100 text-amber-900';
  return 'bg-red-100 text-red-900';
}

function scoreLabel(score: number | null): string {
  return score == null ? '未実施' : `${score.toFixed(0)}点`;
}

/** 教員画面の公開デモ（/demo/teacher/students/:code）。教員メニューの中で表示する */
export function DemoTeacherStudentScores({ participantCode }: { participantCode: string }) {
  return <PublicStudentScoreSample participantCode={participantCode} demoLayout />;
}

export default function PublicStudentScoreSample({
  participantCode,
  demoLayout = false,
}: {
  participantCode: string;
  demoLayout?: boolean;
}) {
  const [, setLocation] = useLocation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['public', 'ranking-sample', 'student', participantCode],
    queryFn: () => getPublicStudentScoreSample(participantCode),
    staleTime: 60 * 1000,
  });
  const backPath = demoLayout ? `${DEMO_TEACHER_BASE}/students` : '/demo/rankings';
  const evaluationPath = (interviewId: number) => demoLayout
    ? demoEvaluationHref(interviewId, participantCode, `${DEMO_TEACHER_BASE}/students/${encodeURIComponent(participantCode)}`)
    : `/evaluation?interview_id=${interviewId}&demo_participant=${encodeURIComponent(participantCode)}`;
  // デモの教員画面は、ランキングと同じく患者面接の点で平均する
  const patientScores = (data?.scores ?? [])
    .map((score) => score.patientScore)
    .filter((score): score is number => typeof score === 'number');
  const averageScore = demoLayout
    ? (patientScores.length > 0 ? patientScores.reduce((sum, score) => sum + score, 0) / patientScores.length : 0)
    : data?.summary.averageScore ?? 0;
  const latestScore = demoLayout ? (data?.scores[0]?.patientScore ?? null) : data?.summary.latestScore ?? null;

  const body = (
    <>
        <button
          type="button"
          onClick={() => setLocation(backPath)}
          className="mb-5 inline-flex min-h-10 items-center gap-2 rounded-md px-3 text-sm font-bold text-cyan-900 hover:bg-cyan-100 focus:outline-none focus:ring-2 focus:ring-cyan-700"
        >
          <ArrowLeft className="h-4 w-4" />
          {demoLayout ? '一覧へ戻る' : 'ランキングへ戻る'}
        </button>

        {isLoading ? (
          <div className="flex min-h-64 items-center justify-center gap-2 text-gray-600">
            <Loader2 className="h-5 w-5 animate-spin" />
            スコアを読み込んでいます...
          </div>
        ) : error || !data ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-red-700">
            この参加者のスコアを表示できませんでした。
          </div>
        ) : (
          <>
            <div className="mb-5">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-black sm:text-3xl">{data.student.name}のスコア</h1>
                {!demoLayout && (
                  <span className="rounded-full bg-cyan-100 px-3 py-1 text-xs font-bold text-cyan-900">
                    公開サンプル
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm text-gray-600">
                学籍番号 {data.student.participantCode}
                {data.student.className ? ` ・ ${data.student.className}` : ''}
              </p>
            </div>

            <div className="mb-5 grid gap-3 sm:grid-cols-3">
              <SummaryCard
                label="評価済み面接"
                value={`${data.summary.evaluatedCount}件`}
                icon={<CalendarDays className="h-5 w-5" />}
              />
              <SummaryCard
                label="平均スコア"
                value={`${averageScore.toFixed(1)}点`}
                icon={<TrendingUp className="h-5 w-5" />}
              />
              <SummaryCard
                label="最新スコア"
                value={scoreLabel(latestScore)}
                icon={<TrendingUp className="h-5 w-5" />}
              />
            </div>

            <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="border-b border-gray-200 px-5 py-4">
                <h2 className="text-lg font-black">面接ごとのスコア</h2>
                <p className="mt-1 text-xs text-gray-500">各面接を押すと、その回の評価結果を確認できます。</p>
              </div>

              {data.scores.length === 0 ? (
                <div className="px-5 py-12 text-center text-sm text-gray-500">
                  表示できる面接結果はありません。
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {data.scores.map((score, index) => (
                    <div
                      key={score.interviewId}
                      role="link"
                      tabIndex={0}
                      onClick={() => setLocation(evaluationPath(score.interviewId))}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setLocation(evaluationPath(score.interviewId));
                        }
                      }}
                      aria-label={`${formatDateTime(score.created)}の面接結果を見る`}
                      className="grid cursor-pointer gap-3 px-5 py-4 hover:bg-cyan-50 focus:bg-cyan-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-700 sm:grid-cols-[minmax(180px,1fr)_repeat(3,minmax(105px,auto))_24px] sm:items-center"
                    >
                      <div>
                        <div className="text-sm font-black text-gray-900">面接 {data.scores.length - index}</div>
                        <div className="mt-1 text-xs font-medium text-gray-500">{formatDateTime(score.created)}</div>
                      </div>
                      <ScoreCell label="総合" score={score.overallScore} />
                      <ScoreCell label="患者面接" score={score.patientScore} />
                      <ScoreCell label="医師報告" score={score.dentistScore} />
                      <ChevronRight className="hidden h-5 w-5 text-gray-400 sm:block" />
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
    </>
  );

  if (demoLayout) return <AdminLayout demo>{body}</AdminLayout>;
  return (

    <div className="min-h-screen bg-gray-50 text-gray-950">
      <div className="border-b border-cyan-900 bg-cyan-950 px-5 py-4 text-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4">
          <div>
            <div className="text-lg font-bold">Dental Avatar</div>
            <div className="text-xs font-medium text-cyan-100">教員ダッシュボード体験</div>
          </div>
          <span className="rounded-full border border-cyan-600 px-3 py-1 text-xs font-bold text-cyan-50">
            閲覧専用
          </span>
        </div>
      </div>

      <main className="mx-auto max-w-5xl px-4 py-7 sm:px-6 lg:px-8">
        {body}
      </main>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: JSX.Element;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-center justify-between gap-3 text-sm font-bold text-gray-600">
        <span>{label}</span>
        <span className="text-cyan-800">{icon}</span>
      </div>
      <div className="mt-3 text-2xl font-black text-gray-900">{value}</div>
    </div>
  );
}

function ScoreCell({ label, score }: { label: string; score: number | null }) {
  return (
    <div className="flex items-center justify-between gap-3 sm:block sm:text-right">
      <div className="text-xs font-bold text-gray-500">{label}</div>
      <span className={cn('mt-1 inline-block rounded-md px-2.5 py-1 text-sm font-black', scoreTone(score))}>
        {scoreLabel(score)}
      </span>
    </div>
  );
}
